import * as yup from "yup";
import { useState } from "react";
import { useFormik } from "formik";
import { tSettings } from "../../../../../../i18n/useAppTranslation";
import { isNullish, passOr } from "../../../../../../utilities/general";
import {
  MULTI_QUESTION_LOGIC_JUMP_TYPES,
  MULTI_QUESTION_TYPES,
} from "../../../../../../api/questionnaires/config";
import { NodeId } from "../../../utilities";
import {
  INITIAL_LOGIC_JUMPS,
  QUESTION_TYPES_WITH_CHOICES,
  QUESTION_TYPES_WITH_LOGIC_JUMPS,
} from "../config";
import { useQuestionSchema } from "../schemas";
import {
  findLogicJumpsByQuestionId,
  getQuestionIdsWithUnfinishedLogicJumps,
} from "../utilities";

function logicJumpsAdapter({ logicNodes, questions }) {
  const jumpNodes = logicNodes.filter(
    (n) => n.type === MULTI_QUESTION_LOGIC_JUMP_TYPES.jump,
  );
  const elseNodes = logicNodes.filter(
    (n) => n.type === MULTI_QUESTION_LOGIC_JUMP_TYPES.else,
  );

  function prepareId(id) {
    if (id) {
      return NodeId.make(id);
    }
    return null;
  }

  function jumpNodeAdapter(node) {
    return {
      type: MULTI_QUESTION_LOGIC_JUMP_TYPES.jump,
      elseQuestionId: null,
      ifSelectedChoiceId: prepareId(node.procedure_question_option_id),
      thenQuestionId: prepareId(questions?.[node.jump_to_question]?.id),
    };
  }

  function elseNodeAdapter(node) {
    return {
      type: MULTI_QUESTION_LOGIC_JUMP_TYPES.else,
      ifSelectedChoiceId: null,
      thenQuestionId: null,
      elseQuestionId: prepareId(questions?.[node.jump_to_question]?.id),
    };
  }

  return [...jumpNodes.map(jumpNodeAdapter), ...elseNodes.map(elseNodeAdapter)];
}

function choicesAdapter(choiceNodes) {
  return choiceNodes.map((c) => ({
    id: NodeId.make(c.id),
    value: c.question_option,
  }));
}

function questionnaireAdapter(questionnaire) {
  const questions = questionnaire?.multi_questions || [];

  return {
    title: questionnaire?.title || "",
    questions:
      questions.map((q) => ({
        id: NodeId.make(q.id),
        question: q.question,
        questionType: q.question_type,
        isRequired: Boolean(q.required),
        description: q.description_text,
        allowCommentsOnScale: passOr(
          q.question_type === MULTI_QUESTION_TYPES.opinionScale,
          false,
          () => Boolean(q.why_choose),
        ),
        logicJumps: passOr(
          QUESTION_TYPES_WITH_LOGIC_JUMPS.includes(q.question_type),
          [],
          () =>
            logicJumpsAdapter({
              logicNodes: q.procedure_templates_logic || [],
              questions,
            }),
        ),
        choices: passOr(
          [
            ...QUESTION_TYPES_WITH_CHOICES,
            MULTI_QUESTION_TYPES.opinionScale,
            MULTI_QUESTION_TYPES.yesNo,
          ].includes(q.question_type),
          [],
          () => choicesAdapter(q.procedure_template_question_option || []),
        ),
      })) || [],
  };
}

// ===============================

export function useForm({ submitter, questionnaire }) {
  const questionSchema = useQuestionSchema();

  const validationSchema = yup.object({
    title: yup
      .string()
      .trim()
      .required(
        tSettings("questionnaires.form.errors.questionnaireNameRequired"),
      ),

    questions: yup
      .array()
      .of(questionSchema)
      .min(1, tSettings("questionnaires.form.errors.questionsMin")),
  });

  const [logicJumpErrorsQuestionIds, setLogicJumpErrorsQuestionIds] = useState(
    [],
  );

  const validateLogicJumps = (questions) => {
    setLogicJumpErrorsQuestionIds([]);

    const questionIdsWithUnfinishedLogicJumps =
      getQuestionIdsWithUnfinishedLogicJumps(questions);

    if (questionIdsWithUnfinishedLogicJumps.length) {
      setLogicJumpErrorsQuestionIds(questionIdsWithUnfinishedLogicJumps);
      return false;
    }

    return true;
  };

  const form = useFormik({
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      if (validateLogicJumps(values.questions)) {
        submitter(values);
      }
    },
    initialValues: questionnaireAdapter(questionnaire),
  });

  function getError(key) {
    if (form.touched[key] && form.errors[key]) {
      switch (key) {
        case "questions": {
          const error = form.errors[key];
          if (Array.isArray(error)) {
            return tSettings("questionnaires.form.errors.innerQuestionsError");
          }
          return error;
        }
        default: {
          return form.errors[key];
        }
      }
    }
    return null;
  }

  return {
    ...form,
    logicJumpErrorsQuestionIds,
    getError,
    questions: {
      value: form.values.questions,
      append: (next) => {
        if (next) {
          form.setFieldTouched("questions");
          form.setFieldValue("questions", [...form.values.questions, next]);
        }
      },
      replace: (next) => {
        if (next) {
          form.setFieldTouched("questions");
          form.setFieldValue("questions", next);
        }
      },
      duplicateByIndex: (index) => {
        if (!isNullish(index) && form.values.questions?.[index]) {
          form.setFieldTouched("questions");
          const duplicate = {
            ...form.values.questions[index],
            logicJumps: INITIAL_LOGIC_JUMPS,
            id: NodeId.make(),
          };
          form.setFieldValue("questions", [
            ...form.values.questions.slice(0, index + 1),
            duplicate,
            ...form.values.questions.slice(index + 1),
          ]);
        }
      },
      deleteByIndex: (index) => {
        if (!isNullish(index)) {
          let questions = form.values.questions;
          const question = questions[index];

          const affectedLogicJumps = findLogicJumpsByQuestionId({
            questions,
            questionId: question.id,
          });

          if (affectedLogicJumps.length) {
            affectedLogicJumps.forEach((jump) => {
              const { questionIndex, logicJumpIndex, logicJumpField } = jump;
              const nextQuestion = JSON.parse(
                JSON.stringify({ ...questions[questionIndex] }),
              );
              nextQuestion.logicJumps[logicJumpIndex][logicJumpField] = null;
              questions = [
                ...questions.slice(0, questionIndex),
                nextQuestion,
                ...questions.slice(questionIndex + 1),
              ];
            });
          }

          const nextQuestions = [
            ...questions.slice(0, index),
            ...questions.slice(index + 1),
          ];

          form.setFieldTouched("questions");
          form.setFieldValue("questions", nextQuestions);

          validateLogicJumps(nextQuestions);
        }
      },
      updateByIndex: (next, index) => {
        if (next) {
          const nextQuestions = [
            ...form.values.questions.slice(0, index),
            next,
            ...form.values.questions.slice(index + 1),
          ];

          form.setFieldTouched("questions");
          form.setFieldValue("questions", nextQuestions);

          validateLogicJumps(nextQuestions);
        }
      },
    },
  };
}
